import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import ReactSelect from 'react-select';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import { deleteUserProfile, getUsers, updateUser, createUser } from '../../lib/apiService';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const ROLE_LABELS = { admin: 'Admin', data_entry: 'Data Entry', sales: 'Sales' };
const ROLE_COLORS = {
  admin: 'border-blue-200 text-blue-700 bg-transparent',
  data_entry: 'border-emerald-200 text-emerald-700 bg-transparent',
  sales: 'border-yellow-300 text-yellow-700 bg-transparent'
};

const editRoleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'data_entry', label: 'Data Entry' },
  { value: 'sales', label: 'Sales' }
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    borderRadius: '0.75rem', // rounded-xl
    fontSize: '14px',
    borderColor: state.isFocused ? '#ee7923' : '#e2e8f0', // orange / slate-200
    backgroundColor: '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(238, 121, 35, 0.2)' : 'none',
    '&:hover': { borderColor: '#cbd5e1' }
  }),
  valueContainer: (base) => ({
    ...base,
    height: '40px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center'
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '40px'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px 8px'
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px 8px'
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '0.75rem',
    zIndex: 9999,
    fontSize: '14px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)'
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
    borderRadius: '0.75rem'
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    padding: '8px 12px',
    backgroundColor: state.isSelected ? '#ee7923' : state.isFocused ? '#f1f5f9' : 'white',
    color: state.isSelected ? 'white' : '#1e293b'
  })
};

const UsersPage = () => {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');

  // Add User Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('data_entry');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!userProfile) return;
    try {
      const res = await getUsers();
      setUsers(res?.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const startEdit = (user) => {
    setEditingUser(user);
    setEditRole(user.role);
    setEditEmail(user.email);
    setEditPassword('');
  };

  const saveUser = async () => {
    if (!editingUser) return;
    try {
      const data = { role: editRole, email: editEmail };
      if (editPassword) data.password = editPassword;
      const res = await updateUser(editingUser.uid, data);
      if (res && res.status === false) {
        toast.error(res.message || 'Failed to update user');
        return;
      }
      toast.success(`User ${editingUser.name} updated`);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to update user');
    }
  };

  const deleteUser = async (user) => {
    if (user.uid === userProfile?.uid) {
      toast.error('Cannot delete yourself');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) return;
    try {
      await deleteUserProfile(user.uid);
      toast.success(`User ${user.name} deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const openAddModal = () => {
    setNewName('');
    setNewEmail('');
    setNewPassword('');
    setNewRole('data_entry');
    setIsAddModalOpen(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim() || !newPassword.trim()) {
      return toast.error('Please fill in all required fields.');
    }

    setIsSubmitting(true);
    try {
      const res = await createUser({
        name: newName.trim(),
        email: newEmail.trim(),
        password: newPassword.trim(),
        role: newRole
      });

      if (res.status) {
        toast.success(res.message || 'User created successfully');
        setIsAddModalOpen(false);
        fetchUsers();
      } else {
        toast.error(res.message || 'Failed to create user');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Users | JoldiPabo Admin</title>
      </Helmet>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            User Management
          </h1>
          <p className="text-sm text-slate-600 mt-1">Manage team members and their roles</p>
        </div>
        <Button onClick={openAddModal} className="gap-2 bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4" /> Add User
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.uid} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.uid}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-[#ee7923]">{u.name?.[0]?.toUpperCase()}</span>
                      </div>
                      <span className="text-sm font-medium text-slate-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {editingUser?.uid === u.uid ? (
                      <div className="flex flex-col gap-2">
                        <Input value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="Email address" className="h-10 text-xs w-48" />
                        <Input type="password" value={editPassword} onChange={e => setEditPassword(e.target.value)} placeholder="New Password (optional)" className="h-10 text-xs w-48" />
                      </div>
                    ) : (
                      u.email
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingUser?.uid === u.uid ? (
                      <div className="flex items-center gap-2">
                         <div className="w-36">
                           <ReactSelect
                             styles={selectStyles}
                             menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                             menuPosition="fixed"
                             options={editRoleOptions}
                             value={editRoleOptions.find(o => o.value === editRole)}
                             onChange={opt => setEditRole(opt?.value || 'data_entry')}
                             isSearchable={false}
                           />
                         </div>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-10 text-xs px-4" onClick={saveUser} data-testid="save-user-btn">Save</Button>
                        <Button size="sm" variant="ghost" className="h-10 text-xs px-4" onClick={() => setEditingUser(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <Badge className={`border shadow-none hover:bg-transparent text-xs ${ROLE_COLORS[u.role] || 'border-slate-200 bg-transparent text-slate-700'}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(u)} data-testid={`edit-user-${u.uid}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      {u.uid !== userProfile?.uid && (
                        <Button variant="ghost" size="sm" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.uid}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && <div className="text-center py-16 text-slate-500">No users found</div>}
      </Card>

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Add New Team Member
              </h2>
              <button onClick={() => setIsAddModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="px-6 py-5 space-y-4">
              <div>
                <Label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</Label>
                <Input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. John Doe"
                  className="rounded-xl"
                  required
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address *</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="rounded-xl"
                  required
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-slate-700 mb-1.5">Password *</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter password"
                  className="rounded-xl"
                  required
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-slate-700 mb-1.5">Role *</Label>
                <ReactSelect
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  options={editRoleOptions}
                  value={editRoleOptions.find(o => o.value === newRole) || editRoleOptions[1]}
                  onChange={opt => setNewRole(opt?.value || 'data_entry')}
                  isSearchable={false}
                />
              </div>

              <div className="pt-2">
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-10" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;

